/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { or } from '@ember/object/computed';
import Component from '@ember/component';
import { isNone } from '@ember/utils';
import { task } from 'ember-concurrency';
import EventBusService, { CONFIRM_TRANSITION, MAIN_NAV_EXPAND, MAIN_NAV_COLLAPSE } from 'cio/services/event-bus';
import isDestroyed from 'cio/utils/is-destroyed';
import { resolve } from 'rsvp';
import { timeout } from 'ember-concurrency';
import config from 'cio/config/environment';
import ApiErrorsService from 'cio/services/api-errors';
import { ApiResponse } from 'cio/services/api';

export const STATUS = {
  DIRTY: 'You have unsaved changes.',
  SAVING: 'Saving…',
  FAILED: 'Oops! We were unable to save your changes. Please try again.',
  INVALID: 'Required fields must be filled out.',
};

/*
  Basic Usage:

  ```handlebars
  {{cio-save-bar
    isDirty=Boolean
    isInvalid=Boolean
    onReset=(action "...")
    onSave=(action "...")}}
  ```
*/

export default class CioSaveBar extends Component {
  @service apiErrors!: ApiErrorsService;
  @service eventBus!: EventBusService;

  isDirty = false;
  isSaving = false;
  isInvalid = false;
  saveFailed = false;
  saveDisabled = false;
  showConfirmModal = false;
  showSaveTooltip = false;
  transition: any = null;
  savedJustNow = false;
  hasReadOnlyPermission = false;
  onSave: (() => Promise<unknown>) | null = null;
  onReset: (() => unknown) | null = null;
  saveTooltipContent = 'Please fix errors before saving.';
  onShowSaveButtonTooltip: (() => unknown) | null = null;
  invalidErrorMessage = STATUS.INVALID;
  isNavOpen = false;

  @computed('saveTooltipContent')
  get actualSaveTooltipContent() {
    if (this.saveTooltipContent) {
      return this.saveTooltipContent;
    }

    if (this.hasReadOnlyPermission) {
      return "You don't have write permissions in this account.";
    }

    return undefined;
  }

  @or('isSaving', 'isInvalid', 'saveDisabled', 'hasReadOnlyPermission') isSaveBarDisabled!: boolean;

  @computed('isSaving', 'isDirty', 'isInvalid', 'saveFailed', 'invalidErrorMessage')
  // eslint-disable-next-line getter-return
  get status() {
    let { isSaving, isDirty, isInvalid, saveFailed } = this;

    if (isInvalid) {
      return this.invalidErrorMessage || STATUS.INVALID;
    }

    if (saveFailed) {
      return STATUS.FAILED;
    }

    if (isSaving) {
      return STATUS.SAVING;
    }

    if (isDirty) {
      return STATUS.DIRTY;
    }

    return;
  }

  didInsertElement() {
    super.didInsertElement();
    this.eventBus.subscribe(CONFIRM_TRANSITION, this, this._confirmTransition);
    this.eventBus.subscribe(MAIN_NAV_EXPAND, this, this.toggleIsNavOpen);
    this.eventBus.subscribe(MAIN_NAV_COLLAPSE, this, this.toggleIsNavOpen);
  }

  willDestroyElement() {
    super.willDestroyElement();
    this.eventBus.unsubscribe(CONFIRM_TRANSITION, this, this._confirmTransition);
    this.eventBus.unsubscribe(MAIN_NAV_EXPAND, this, this.toggleIsNavOpen);
    this.eventBus.unsubscribe(MAIN_NAV_COLLAPSE, this, this.toggleIsNavOpen);
  }

  _confirmTransition(transition: any) {
    this.setProperties({
      transition,
      showConfirmModal: true,
    });
  }

  _justSavedTimer = task(async () => {
    await timeout(config.APP.saveBarStatusDuration);
  });

  _saveChanges() {
    this.set('isSaving', true);

    let savePromise = this.onSave!();

    savePromise
      .then(
        () => {
          this.apiErrors.clear();
          if (!isDestroyed(this)) {
            this._justSavedTimer.perform();
            this.set('saveFailed', false);
          }
        },
        (response: ApiResponse) => {
          if (!isNone(response)) {
            this.apiErrors.report(response);
          }
          if (!isDestroyed(this)) {
            this.set('saveFailed', true);
          }
        },
      )
      .finally(() => {
        if (!isDestroyed(this)) {
          this.set('isSaving', false);
        }
      });

    return savePromise;
  }

  // onReset could return a promise or it may not. Make sure we're handling
  // both the cases here.
  _discardChanges() {
    return resolve(this.onReset!());
  }

  @action
  saveChanges() {
    this._saveChanges();
  }

  @action
  discardChanges() {
    this._discardChanges().then(() => {
      this.apiErrors.clear();
    });
  }

  @action
  confirmSave() {
    this.set('showConfirmModal', false);
    this._saveChanges().then(() => {
      let { transition } = this;

      if (!isNone(transition)) {
        transition.retry();
      }
    });
  }

  @action
  confirmDiscard() {
    this.set('showConfirmModal', false);
    this._discardChanges().then(() => {
      if (this.transition) {
        this.transition.retry();
      }
    });
  }

  @action
  closeConfirmModal() {
    this.set('showConfirmModal', false);
  }

  @action
  toggleIsNavOpen(event: typeof MAIN_NAV_EXPAND | typeof MAIN_NAV_COLLAPSE) {
    this.set('isNavOpen', event === MAIN_NAV_EXPAND);
  }

  @action
  _onShowSaveButtonTooltip() {
    this.onShowSaveButtonTooltip?.();
  }
}
